<template>
  <DenseExpansionPanel>
    <VExpansionPanelHeader>
      <VSubheader class="justify-space-between">
        <div>
          <OverallStatusIcon
            :status="orderOverallStatus"
            :days-late="maxDaysLate(order.shipments, proofs)"
            tooltip-analytics-name="Overall order status"
          />
          <a
            :href="salesforceUrl(salesforceOrder.id)"
            target="_blank"
            rel="noopener"
            v-text="splitOrderNumber(order.orderNumber)"
          />
          <!--
          CAUTION: The current layout can't fit more than 2 icons, so we're already maxed out.
          Tooltip color is darkened to improve readability and contrast.
          -->
          <IconWithTooltip
            v-if="
              salesforceOrder.orderSpecialInstructions &&
              salesforceOrder.orderSpecialInstructions.length
            "
            icon="$sticky-note"
            color="blue"
            right
            tooltip-analytics-name="Order icon"
            tooltip-color="grey darken-4"
          >
            <h5 class="text-subtitle-1">
              {{
                pluralize(
                  salesforceOrder.orderSpecialInstructions.length,
                  'order special instruction'
                )
              }}
            </h5>
            <!-- Don't wrap in VList because that adds a solid white background color -->
            <SpecialInstructionListItems
              dark
              :special-instructions="salesforceOrder.orderSpecialInstructions"
            />
          </IconWithTooltip>
          <IconWithTooltip
            v-if="shippingType === 'Overnight' || shippingType === '2nd Day'"
            :icon="shippingType === 'Overnight' ? '$fighter-jet' : '$plane'"
            right
            tooltip-analytics-name="Order icon"
          >
            {{ shippingType }}
            Shipping
          </IconWithTooltip>
        </div>
        <div :title="formatDateVerbose(order.created)">
          {{ formatDateDistanceToNowIfRecent(order.created) }}
        </div>
      </VSubheader>
    </VExpansionPanelHeader>
    <VExpansionPanelContent>
      <template
        v-for="orderItem in showAddOns
          ? sortedOrderItemsWithAddOns
          : mainOrderItems"
      >
        <VDivider :key="orderItem.salesforceId + '-divider'" />
        <OrderItemListItem
          :key="orderItem.salesforceId + '-order-item'"
          :class="{
            'search-highlight': isItemHighlighted(orderItem.proofId)
          }"
          :order-item="orderItem"
          :order-item-data="orderItemData(orderItem, order.shipments, proofs)"
        />
      </template>
    </VExpansionPanelContent>
  </DenseExpansionPanel>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import DenseExpansionPanel from '@/components/DenseExpansionPanel.vue'
import {
  formatDateDistanceToNowIfRecent,
  formatDateVerbose
} from '@/components/mixins/dateFormatters'
import { orderUtils } from '@/components/mixins/orderUtils'
import { pluralize } from '@/components/mixins/pluralize'
import SpecialInstructionListItems from '@/components/SpecialInstructionListItems.vue'
import { Order as SalesforceOrder, Proof } from '@/generated'
import { salesforceUrl } from '@/store/modules/salesforce'
import { Order } from '@/store/modules/types/order'

import IconWithTooltip from './IconWithTooltip.vue'
import OrderItemListItem from './OrderItemListItem.vue'
import OverallStatusIcon from './OverallStatusIcon.vue'

export default defineComponent({
  components: {
    DenseExpansionPanel,
    IconWithTooltip,
    OrderItemListItem,
    OverallStatusIcon,
    SpecialInstructionListItems
  },
  props: {
    order: {
      type: Object as PropType<Order>,
      required: true
    },
    salesforceOrder: {
      type: Object as PropType<SalesforceOrder>,
      required: true
    },
    showAddOns: {
      type: Boolean,
      required: true
    },
    proofs: {
      type: Array as PropType<Proof[]>,
      required: true
    },
    allOrders: {
      type: Array as PropType<Order[]>,
      default: () => []
    },
    filterProofIds: {
      type: Array as PropType<string[] | null>,
      default: null
    }
  },
  setup(props) {
    const filterSet = computed(() =>
      props.filterProofIds ? new Set(props.filterProofIds) : null
    )
    return {
      formatDateDistanceToNowIfRecent,
      formatDateVerbose,
      pluralize,
      salesforceUrl,
      ...orderUtils(props.order, props.allOrders),
      shippingType: props.order.shippingType?.type,
      isItemHighlighted(proofId: string) {
        return filterSet.value !== null && filterSet.value.has(proofId)
      }
    }
  }
})
</script>
